import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { getFAQ, createFAQ, updateFAQ, deleteFAQ } from '@/lib/api';

const blankFaqDraft = {
  question: '',
  answer: '',
  order: 0,
  is_active: true
};

export default function AdminFAQ() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [removePromptOpen, setRemovePromptOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [draft, setDraft] = useState(blankFaqDraft);

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    try {
      const res = await getFAQ();
      setItems(res.data.sort((a, b) => a.order - b.order));
    } catch (err) {
      toast.error('Не удалось загрузить FAQ');
    } finally {
      setBusy(false);
    }
  };

  const startCreate = () => {
    setActiveRow(null);
    setDraft({ ...blankFaqDraft, order: items.length });
    setEditorOpen(true);
  };

  const startEdit = (item) => {
    setActiveRow(item);
    setDraft({
      question: item.question,
      answer: item.answer,
      order: item.order,
      is_active: item.is_active
    });
    setEditorOpen(true);
  };

  const askDelete = (item) => {
    setActiveRow(item);
    setRemovePromptOpen(true);
  };

  const persistDraft = async () => {
    if (!draft.question || !draft.answer) {
      toast.error('Укажите вопрос и ответ');
      return;
    }

    try {
      if (activeRow) {
        await updateFAQ(activeRow.id, draft);
        toast.success('FAQ обновлён');
      } else {
        await createFAQ(draft);
        toast.success('FAQ создан');
      }
      setEditorOpen(false);
      fetchRows();
    } catch (err) {
      toast.error('Не удалось сохранить');
    }
  };

  const performDelete = async () => {
    if (!activeRow) return;

    try {
      await deleteFAQ(activeRow.id);
      toast.success('FAQ удалён');
      setRemovePromptOpen(false);
      fetchRows();
    } catch (err) {
      toast.error('Не удалось удалить');
    }
  };

  if (busy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-faq">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">FAQ</h1>
          <p className="text-slate-500">Частые вопросы и ответы</p>
        </div>
        <Button onClick={startCreate} data-testid="add-faq-button">
          <Plus className="w-4 h-4 mr-2" />
          Добавить
        </Button>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <HelpCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Нет вопросов</p>
            <Button variant="outline" className="mt-4" onClick={startCreate}>
              Добавить первый вопрос
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item.id} data-testid={`faq-card-${item.id}`}>
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900">{item.question}</h3>
                      {!item.is_active && (
                        <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-500 rounded-full">Скрыт</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{item.answer}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startEdit(item)}
                      data-testid={`edit-faq-${item.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => askDelete(item)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-faq-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{activeRow ? 'Редактировать вопрос' : 'Новый вопрос'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Вопрос *</Label>
              <Input
                value={draft.question}
                onChange={(e) => setDraft({ ...draft, question: e.target.value })}
                placeholder="Это официальное трудоустройство?"
                data-testid="faq-question-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Ответ *</Label>
              <Textarea
                value={draft.answer}
                onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
                placeholder="Да, мы работаем только с официальными работодателями..."
                rows={4}
                data-testid="faq-answer-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Активен</Label>
              <Switch
                checked={draft.is_active}
                onCheckedChange={(checked) => setDraft({ ...draft, is_active: checked })}
                data-testid="faq-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Отмена
            </Button>
            <Button onClick={persistDraft} data-testid="save-faq-button">
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={removePromptOpen} onOpenChange={setRemovePromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить вопрос?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить этот вопрос? Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-faq"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
